import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import {
    Button,
    HorizontalContainer,
    Modal,
    VerticalContainer,
} from '@/components';
import { EditIcon, GreenDotIcon, RedDotIcon } from '@/components/Icons';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { SubscriptionDetailsResponse } from '@/features/profile/model/types';
import { Log } from '@/shared/Log';

interface SubscriptionDetailsProps {
    subscriptions?: SubscriptionDetailsResponse[];
}

export const SubscriptionDetails = ({
    subscriptions,
}: SubscriptionDetailsProps) => {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [searchParams] = useSearchParams();
    const subscriptionId = searchParams.get('subscriptionId');
    const subscription = subscriptions?.find(
        (s) => s.SubscriptionId === subscriptionId,
    );

    if (!subscriptionId || !subscription) {
        Log.error(
            `SubscriptionDetails: subscriptionId is missing or invalid. subscriptionId=${subscriptionId}`,
        );
        return <Navigate to="/Access" replace />;
    }

    const showUnsubscribeButton =
        !subscription.IsSinglePayment &&
        !subscription.SubscriptionExpired &&
        !subscription.IsCancelled;
    const canUpdatePaymentInfo =
        !subscription.IsCancelled &&
        !subscription.SubscriptionExpired &&
        subscription.PaymentInfoLink &&
        !subscription.IsCoveredByCoupon;

    return (
        <>
            <VerticalContainer
                spacing="sm"
                className="w-full justify-between md:h-full"
            >
                <VerticalContainer spacing="md">
                    <HorizontalContainer
                        spacing="xs"
                        className="items-center text-sm"
                    >
                        {subscription.IsActive && (
                            <GreenDotIcon className="size-2" />
                        )}
                        {!subscription.IsActive && (
                            <RedDotIcon className="size-2" />
                        )}
                        <span>{subscription.StatusText}</span>
                    </HorizontalContainer>

                    <VerticalContainer spacing="sm">
                        <h2 id="heading-SubscriptionDetails">
                            {subscription.Title}
                        </h2>
                        {!subscription.IsCoveredByCoupon && (
                            <div className="text-lg text-(--color-text-secondary)">
                                {subscription.Price} {subscription.PricePeriod}
                            </div>
                        )}
                    </VerticalContainer>

                    <VerticalContainer spacing="sm">
                        {!subscription.IsCoveredByCoupon && (
                            <HorizontalContainer className="justify-between text-lg font-bold">
                                <span>{subscription.NextPaymentText}:</span>
                                <span>{subscription.NextPayment}</span>
                            </HorizontalContainer>
                        )}
                        {!subscription.IsSinglePayment &&
                            !subscription.IsCoveredByCoupon && (
                                <HorizontalContainer className="justify-between text-lg">
                                    <span>
                                        {R.PaymentProfileLastPaymentDate}
                                    </span>
                                    <span>{subscription.LatestPayment}</span>
                                </HorizontalContainer>
                            )}

                        <HorizontalContainer className="justify-between text-lg">
                            <span>{R.PaymentProfileStart}</span>
                            <span>{subscription.StartedOn}</span>
                        </HorizontalContainer>

                        {subscription.IsCancelled && (
                            <HorizontalContainer className="justify-between text-lg">
                                <span>{R.PaymentProfileCanceledAt}:</span>
                                <span>{subscription.CancelledOn}</span>
                            </HorizontalContainer>
                        )}

                        {!subscription.IsSubscriptionPlanActive && (
                            <div className="text-lg leading-normal">
                                {R.SubscriptionPlanDiscontinued}
                            </div>
                        )}
                    </VerticalContainer>

                    {canUpdatePaymentInfo && (
                        <a
                            className="flex min-h-18 w-full items-center justify-between rounded-2xl border border-(--color-border-muted) px-4 no-underline hover:bg-(--color-bg-subtle) lg:w-1/2"
                            href={subscription.PaymentInfoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="text-lg font-bold">
                                {R.UpdatePaymentInfo}
                            </span>
                            <EditIcon />
                        </a>
                    )}
                </VerticalContainer>

                {subscription.AllowUncancel && (
                    <form
                        method="post"
                        action="/payment/subscribe/uncancel"
                        className="self-center md:self-end"
                    >
                        <input
                            type="hidden"
                            name="subscriptionId"
                            value={subscription.SubscriptionId}
                        />
                        <Button type="submit" variant="danger">
                            {R.Regret}
                        </Button>
                    </form>
                )}

                {showUnsubscribeButton && (
                    <Button
                        variant="danger"
                        className="self-center md:self-end"
                        onClick={() => setShowCancelModal(true)}
                    >
                        {R.Unsubscribe} {subscription.Title}
                    </Button>
                )}
            </VerticalContainer>

            {showCancelModal && (
                <Modal
                    title={R.SubscribeCancelTitle}
                    onClose={() => setShowCancelModal(false)}
                >
                    <VerticalContainer spacing="lg">
                        <p className="leading-normal">
                            {R.SubscribeCancelCopy}
                        </p>
                        <form
                            method="post"
                            action="/payment/subscribe/cancel"
                            className="flex flex-wrap justify-end gap-4"
                        >
                            <input
                                type="hidden"
                                name="subscriptionId"
                                value={subscription.SubscriptionId}
                            />
                            <Button
                                variant="ghost"
                                onClick={() => setShowCancelModal(false)}
                            >
                                {R.Keep}
                            </Button>
                            <Button variant="danger" type="submit">
                                {R.Unsubscribe}
                            </Button>
                        </form>
                    </VerticalContainer>
                </Modal>
            )}
        </>
    );
};
